import type {FontKey} from '@features/api/chat/schema';

type FontValue = {style: {fontFamily: string}; className: string};

export const colors = [
  {name: 'Old Banani', value: '#242424'}, // Rotten Banani, Blackcurrant
  {name: 'Grape', value: '#634AFF'},
  {name: 'Blueberry', value: '#284CFF'},
  {name: 'Kiwi', value: '#00C846'},
  {name: 'Mint', value: '#00B09F'},
  {name: 'Persimmon', value: '#FF5B27'}, // Orange
  {name: 'Dragon Fruit', value: '#F54997'},
  {name: 'Strawberry', value: '#FF272A'},
];

const font = (fontFamily: string): FontValue => ({
  style: {fontFamily},
  className: '',
});

export const fonts: {
  [f in FontKey]: {
    name: string;
    value: FontValue;
  };
} = {
  // Curated Dinamo set — admin-Taste flows reference these. @font-face
  // declarations live in app/globals.css so the browser resolves them.
  abcWhyte: {
    name: 'ABC Whyte Mono Variable',
    value: font('"ABC Whyte Mono Variable", monospace'),
  },
  abcMarfaMono: {
    name: 'ABC Marfa Mono Variable',
    value: font('"ABC Marfa Mono Variable", monospace'),
  },
  abcSocial: {
    name: 'ABC Social Plus Variable',
    value: font('"ABC Social Plus Variable", sans-serif'),
  },
  abcSolar: {
    name: 'ABC Solar Variable',
    value: font('"ABC Solar Variable", sans-serif'),
  },
  abcDiatypeRounded: {
    name: 'ABC Diatype Rounded Plus Variable',
    value: font('"ABC Diatype Rounded Plus Variable", sans-serif'),
  },
  abcDiatype: {
    name: 'ABC Diatype Plus Variable',
    value: font('"ABC Diatype Plus Variable", sans-serif'),
  },
  abcGravity: {
    name: 'ABC Gravity Variable',
    value: font('"ABC Gravity Variable", sans-serif'),
  },
  abcOtto: {
    name: 'ABC Otto Variable',
    value: font('"ABC Otto Variable", sans-serif'),
  },
  abcGramercy: {
    name: 'ABC Gramercy Standard Variable',
    value: font('"ABC Gramercy Standard Variable", serif'),
  },
  abcMarist: {
    name: 'ABC Marist Variable',
    value: font('"ABC Marist Variable", serif'),
  },
  abcFavorit: {
    name: 'ABC Favorit Variable',
    value: font('"ABC Favorit Variable", sans-serif'),
  },
  abcFavoritMono: {
    name: 'ABC Favorit Mono Variable',
    value: font('"ABC Favorit Mono Variable", monospace'),
  },
  // Non-ABC display faces bundled for Taste flows (see dinamoFontFaceCss.ts).
  futuraPt: {
    name: 'Futura PT Medium',
    value: font('"Futura PT Medium", sans-serif'),
  },
  fonseca: {
    name: 'Fonseca Bold',
    value: font('"Fonseca Bold", serif'),
  },
  novaDeco: {
    name: 'NovaDeco Bold',
    value: font('"NovaDeco Bold", sans-serif'),
  },
  // Legacy Google fonts — kept for canvas users + existing themes.
  inter: {name: 'Inter', value: font('Inter, sans-serif')},
  dmSans: {name: 'DM Sans', value: font('"DM Sans", sans-serif')},
  nunito: {name: 'Nunito', value: font('Nunito, sans-serif')},
  ptSerif: {name: 'PT Serif', value: font('"PT Serif", serif')},
  spaceGrotesk: {
    name: 'Space Grotesk',
    value: font('"Space Grotesk", sans-serif'),
  },
  ibmPlexMono: {
    name: 'IBM Plex Mono',
    value: font('"IBM Plex Mono", monospace'),
  },
  ibmPlexSans: {
    name: 'IBM Plex Sans',
    value: font('"IBM Plex Sans", sans-serif'),
  },
  robotoSlab: {name: 'Roboto Slab', value: font('"Roboto Slab", serif')},
  geist: {name: 'Geist', value: font('Geist, sans-serif')},
  shantellSans: {
    name: 'Shantell Sans',
    value: font('"Shantell Sans", cursive'),
  },
} as const;

// Fonts shown in the canvas theme editor's font picker. Dinamo families are
// loaded above for back-compat (existing admin-Taste flows render correctly),
// but they are admin-panel-only and not surfaced to canvas users.
export const fontDisplayKeys: readonly FontKey[] = [
  'inter',
  'dmSans',
  'nunito',
  'ptSerif',
  'spaceGrotesk',
  'ibmPlexMono',
  'ibmPlexSans',
  'robotoSlab',
  'geist',
  'shantellSans',
];

// All font keys — used by the Components-genType Assets picker only when the
// current flow is admin-Taste (so admins can pick Dinamo families too).
export const fontAllKeys: readonly FontKey[] = [
  'abcWhyte',
  'abcMarfaMono',
  'abcSocial',
  'abcDiatypeRounded',
  'abcDiatype',
  'abcGravity',
  'abcOtto',
  'abcSolar',
  'abcGramercy',
  'abcMarist',
  'abcFavorit',
  'abcFavoritMono',
  'futuraPt',
  'fonseca',
  'novaDeco',
  ...fontDisplayKeys,
];
